import XLSX from 'xlsx-js-style';

// Mismo orden y nombres que el Excel con el que se cotiza
const COLUMNAS = [
    { header: '#', key: 'id', width: 7, tipo: 'entero' },
    { header: 'Ruta', key: 'nombre', width: 30 },
    { header: 'Cliente', key: 'clienteNombre', width: 24 },
    { header: 'Unidad', key: 'tipoUnidadNombre', width: 18 },
    { header: 'Precio por viaje', key: 'precioPorViaje', width: 14, tipo: 'moneda' },
    { header: 'Viajes Semanales', key: 'viajesSemanales', width: 12, tipo: 'entero' },
    { header: 'Comentarios', key: 'comentarios', width: 30 },
    { header: 'Kms de ruta', key: 'kilometros', width: 11, tipo: 'decimal' },
    { header: 'Kms anteriores', key: 'kilometrosAnteriores', width: 12, tipo: 'decimal', anterior: true },
    { header: 'Precio anterior', key: 'precioAnterior', width: 14, tipo: 'moneda', anterior: true },
    { header: 'Estatus', key: 'estatus', width: 22 },
    { header: 'Fecha de creación', key: 'fechaCreacion', width: 14, tipo: 'fecha' },
    { header: 'Fecha tentativa', key: 'fechaTentativa', width: 14, tipo: 'fecha' },
];

const COLOR = {
    encabezado: 'C65911',
    texto: '1F2937',
    suave: '6B7280',
    borde: 'BFBFBF',
    anterior: 'D9D9D9',
};

const FORMATO = {
    moneda: '"$"#,##0.00',
    decimal: '#,##0.00',
    entero: '0',
    fecha: 'dd/mm/yyyy',
};

const LINEA = { style: 'thin', color: { rgb: COLOR.borde } };
const BORDES = { top: LINEA, bottom: LINEA, left: LINEA, right: LINEA };
const FUENTE = { name: 'Calibri', sz: 11, color: { rgb: COLOR.texto } };

const ESTILO_TITULO = { font: { ...FUENTE, sz: 16, bold: true, color: { rgb: COLOR.encabezado } } };
const ESTILO_SUBTITULO = { font: { ...FUENTE, sz: 10, color: { rgb: COLOR.suave } } };
const ESTILO_ENCABEZADO = {
    font: { ...FUENTE, bold: true, color: { rgb: 'FFFFFF' } },
    fill: { fgColor: { rgb: COLOR.encabezado } },
    alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
    border: BORDES,
};

const FILA_ENCABEZADO = 3;

const celda = (r, c) => XLSX.utils.encode_cell({ r, c });

// El backend manda las fechas como "dd/MM/yyyy" o "dd/MM/yyyy HH:mm"; se pasan a número de Excel
// (sin hora) para que se puedan ordenar y filtrar
const fechaExcel = (fecha) => {
    const [d, m, y] = (fecha ?? '').slice(0, 10).split('/').map(Number);
    if (!d || !m || !y) return '';
    return (Date.UTC(y, m - 1, d) - Date.UTC(1899, 11, 30)) / 86400000;
};

const valorCelda = (cotizacion, col) => {
    const valor = cotizacion[col.key];
    if (col.tipo === 'fecha') return fechaExcel(valor);
    if (col.tipo && valor != null && valor !== '') return Number(valor);
    return valor ?? '';
};

const estiloDato = (col) => ({
    font: FUENTE,
    border: BORDES,
    alignment: {
        vertical: 'center',
        wrapText: !col.tipo,
        horizontal: col.tipo ? 'center' : 'left',
    },
    ...(col.anterior && { fill: { fgColor: { rgb: COLOR.anterior } } }),
});

/**
 * Exporta el listado de cotizaciones a Excel con los datos que se usan para cotizar
 * (precio, viajes, kms y valores anteriores) para no tener que abrir cada cotización.
 */
export const exportarCotizacionesExcel = (cotizaciones) => {
    const ultimaCol = COLUMNAS.length - 1;
    const ultimaFila = FILA_ENCABEZADO + cotizaciones.length;

    const filas = [
        ['Cotizaciones empresariales'],
        [`Generado el ${new Date().toLocaleString('es-MX')}   |   ${cotizaciones.length} cotizaciones`],
        [],
        COLUMNAS.map((c) => c.header),
        ...cotizaciones.map((cot) => COLUMNAS.map((c) => valorCelda(cot, c))),
    ];

    const ws = XLSX.utils.aoa_to_sheet(filas);

    ws[celda(0, 0)].s = ESTILO_TITULO;
    ws[celda(1, 0)].s = ESTILO_SUBTITULO;

    COLUMNAS.forEach((col, c) => {
        ws[celda(FILA_ENCABEZADO, c)].s = ESTILO_ENCABEZADO;

        cotizaciones.forEach((_, i) => {
            const ref = celda(FILA_ENCABEZADO + 1 + i, c);
            ws[ref].s = estiloDato(col);
            if (col.tipo && ws[ref].v !== '') ws[ref].z = FORMATO[col.tipo];
        });
    });

    ws['!merges'] = [0, 1].map((r) => ({ s: { r, c: 0 }, e: { r, c: ultimaCol } }));
    ws['!cols'] = COLUMNAS.map((c) => ({ wch: c.width }));
    ws['!rows'] = [{ hpt: 26 }, { hpt: 16 }, { hpt: 8 }, { hpt: 32 }];
    if (cotizaciones.length) {
        ws['!autofilter'] = { ref: `${celda(FILA_ENCABEZADO, 0)}:${celda(ultimaFila, ultimaCol)}` };
    }

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Cotizaciones');

    const hoy = new Date().toLocaleDateString('en-CA'); // YYYY-MM-DD
    XLSX.writeFile(wb, `Cotizaciones_${hoy}.xlsx`);
};
